import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  Users,
  ChevronRight,
  Check,
  Image as ImageIcon,
} from "lucide-react";
import Images from "../../utils/Images";

const Hero = () => {
  const [imageError, setImageError] = useState(false);
  const [imageError2, setImageError2] = useState(false);
  const [, setPartnerImageErrors] = useState({
    diageo: false,
    nestle: false,
    samsung: false,
    indomie: false,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const featureItems = [
    {
      icon: <MessageSquare className="w-5 h-5 text-red-600" />,
      text: "Bulk SMS",
    },
    {
      icon: <Users className="w-5 h-5 text-red-600" />,
      text: "Personalized Messaging",
    },
  ];

  const handleImageError = () => {
    setImageError(true);
  };

  const handleFallbackImageError = () => {
    setImageError2(true);
  };

  const handlePartnerImageError = (partner) => {
    setPartnerImageErrors((prev) => ({
      ...prev,
      [partner]: true,
    }));
  };

  return (
    <div className="overflow-hidden relative bg-gradient-to-br from-red-50 via-white to-red-50">
      {/* Decorative elements */}
      <div className="overflow-hidden absolute top-0 left-0 z-0 w-full h-full">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-red-100 rounded-full opacity-70 mix-blend-multiply filter blur-xl animate-spin-slow"></div>
        <div className="absolute -right-24 top-96 w-80 h-80 bg-red-200 rounded-full opacity-70 mix-blend-multiply filter blur-xl"></div>
        <div className="absolute left-12 bottom-24 w-64 h-64 bg-yellow-100 rounded-full opacity-60 mix-blend-multiply filter blur-xl"></div>
      </div>

      <div className="relative z-10 px-4 py-12 mx-auto max-w-8xl sm:px-6 lg:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-8 items-center lg:grid-cols-2">
          {/* Left column - Text content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="py-12 space-y-6 md:space-y-8"
          >
            <motion.h1
              variants={itemVariants}
              className="text-4xl font-bold leading-loose text-gray-900 md:text-5xl lg:text-7xl"
            >
              <span className="text-red-600">Engage</span> Your Audience
              <br />
              At Scale With
              <br />
              <span className="text-red-600">TXTConnect</span>
            </motion.h1>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-3 items-center text-gray-700"
            >
              <span className="text-lg font-medium">Using</span>
              {featureItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-1">
                  {index > 0 && <span className="text-gray-400">/</span>}
                  <div className="flex items-center px-3 py-1 bg-white rounded-full border border-gray-100 shadow-sm">
                    {item.icon}
                    <span className="ml-1.5 text-sm font-medium">
                      {item.text}
                    </span>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.p
              variants={itemVariants}
              className="max-w-lg text-base text-gray-600 md:text-base"
            >
              TXTConnect provides specialized communication tools for businesses
              of all sizes that help simplify relationship building and drive
              revenue.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <Link
                to="/signup"
                className="flex justify-center items-center px-8 py-3 text-base font-medium text-white bg-red-600 rounded-lg transition-colors duration-200 hover:bg-red-700"
              >
                Get Started
                <ChevronRight className="ml-1 w-5 h-5" />
              </Link>

              <Link
                to="/contact-us"
                className="flex justify-center items-center px-8 py-3 text-base font-medium text-gray-800 bg-white rounded-lg border border-gray-200 transition-colors duration-200 hover:bg-gray-50"
              >
                Contact Us
              </Link>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-4">
              <p className="mb-3 font-medium text-gray-600">
                Our Trusted Partners
              </p>
              <div className="flex flex-wrap gap-6 items-center">
                <img
                  src={Images.partners_img}
                  alt="partners"
                  className="object-contain h-16 opacity-70 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
                  onError={() => handlePartnerImageError("partners")}
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Right column - Image/Illustration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              <motion.div
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ duration: 1 }}
                className="overflow-hidden relative z-20 rounded-2xl shadow-2xl"
              >
                {!imageError ? (
                  <img
                    src={Images.heroImage}
                    alt="African women using mobile phones"
                    className="object-cover w-full h-auto rounded-2xl"
                    onError={handleImageError}
                  />
                ) : !imageError2 ? (
                  <img
                    src={Images.fallbackHero}
                    alt="African women using mobile phones"
                    className="object-cover w-full h-auto rounded-2xl"
                    onError={handleFallbackImageError}
                  />
                ) : (
                  <div className="flex flex-col justify-center items-center w-full h-96 bg-red-50 rounded-2xl">
                    <div className="mb-4">
                      <img
                        src={Images.placeholderHero}
                        alt="Placeholder"
                        className="w-40 h-auto rounded opacity-70"
                      />
                    </div>
                    <div className="mb-2 text-red-600">
                      <ImageIcon size={36} />
                    </div>
                    <p className="px-4 text-center text-gray-600">
                      Visualize effortless communication with TXTConnect
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Floating message cards */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="absolute top-10 -left-10 z-30 bg-white rounded-lg shadow-lg p-4 max-w-[230px]"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-full">
                    <div className="flex justify-center items-center w-6 h-6 bg-red-600 rounded-full">
                      <span className="text-xs text-white">TXT</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">TXTConnect</h3>
                    <p className="mt-1 text-xs text-gray-600">
                      Thank you all for attending the annual office brunch
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.8 }}
                className="absolute bottom-20 -right-5 z-30 bg-white rounded-lg shadow-lg p-4 max-w-[230px]"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-full">
                    <div className="flex justify-center items-center w-6 h-6 bg-red-600 rounded-full">
                      <span className="text-xs text-white">TXT</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">TXTConnect</h3>
                    <p className="mt-1 text-xs text-gray-600">
                      Your package has been shipped and will arrive by Friday
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1 }}
                className="absolute -bottom-10 left-10 z-30 p-4 bg-white rounded-lg shadow-lg"
              >
                <h3 className="text-sm font-semibold text-gray-800">
                  Message Stats
                </h3>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-red-500 mr-1.5" />
                    <span className="text-xs">98% Delivery rate</span>
                  </div>
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-red-500 mr-1.5" />
                    <span className="text-xs">45% Response rate</span>
                  </div>
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-red-500 mr-1.5" />
                    <span className="text-xs">10K+ Messages sent</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Background decorative elements */}
            <div className="absolute -top-16 -right-16 z-10 w-64 h-64 bg-yellow-100 rounded-full opacity-70 mix-blend-multiply filter blur-xl"></div>
            <div className="absolute -bottom-16 -left-16 z-10 w-64 h-64 bg-red-100 rounded-full opacity-70 mix-blend-multiply filter blur-xl"></div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
